<template>
  <div class="job-queue color-white">
    <v-row align="center" class="job-queue-header ma-0 px-4" dense>
      <v-col class="shrink">
        <TableSearch
          :search-value="scheduleFilterSearch"
          label="Search queue"
          @update-search="handleUpdateSearch"
        />
      </v-col>
      <v-slide-x-transition>
        <v-col class="shrink">
          <PriorityFilter
            :mandatory="false"
            :selected-priorities="selectedPriorities"
            @on-change="handleUpdatePriorities"
          />
        </v-col>
      </v-slide-x-transition>
      <v-col class="shrink">
        <ColumnSelector />
      </v-col>
    </v-row>
    <v-data-table
      :header-props="{ sortIcon: 'mdi-menu-up' }"
      :headers="headers"
      :item-class="({ priority, status }) => ['table-row', priority.name, status]"
      :items="enrichedJobs"
      :items-per-page="query.limit"
      :loading="isFetching"
      :sort-by.sync="query.sortBy"
      :sort-desc.sync="query.sortDesc"
      class="jobs-table"
      data-test="jobs-table"
      fixed-header
      hide-default-footer
      loading-text="Loading..."
      mobile-breakpoint="0"
      multi-sort
    >
      <template #no-data>
        <div class="d-flex align-center">
          <vs-text type="subdued">No jobs found. Have you tried checking your filters? </vs-text>
          <vs-button
            class="ml-4"
            label="Reset filters"
            small
            type="secondary"
            @click="resetAllDayViewFilters"
          />
        </div>
      </template>
      <template #progress>
        <v-progress-linear color="blue" indeterminate />
      </template>

      <template #item="{ item }">
        <tr
          :key="item.id"
          :class="{
            'jobs-table-row-selected': item.number === selectedJobNumber,
          }"
          class="text-no-wrap"
          data-test="jobs-table-row"
          @click="handleRowClicked(item)"
        >
          <template v-for="column in headers">
            <job-column
              v-if="column.isCustom"
              :key="`${item.id}__${column.value}__custom`"
              :component="column.component[item.hub_id]"
              :formatter="column.formatter[item.hub_id]"
              :job="item"
              :value="column.value"
            />
            <job-column
              v-else
              :key="`${item.id}__${column.value}`"
              :component="column.component"
              :formatter="column.formatter"
              :job="item"
              :value="column.value"
            />
          </template>
        </tr>
      </template>
    </v-data-table>
    <div>
      <v-col class="job-queue-footer">
        <v-row align="center" class="white" dense justify="space-between">
          <job-search-pagination
            :current-page="getCurrentPage"
            :query="query"
            :update-page="updatePage"
          />
          <v-col v-if="$vuetify.breakpoint.smAndUp" class="shrink">
            <v-row align="center" class="fill-height" dense>
              <v-col class="shrink">
                <v-skeleton-loader v-if="countFetching" rounded type="button" />
                <vs-text v-else data-test="jobs-results-count">{{ count }} results </vs-text>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </div>
    <vs-snackbar v-model="showSnackbar" type="error">
      {{ searchError }}
    </vs-snackbar>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, watchEffect } from 'vue'
import { useState } from 'vuex-composition-helpers'

import { useJobsSearch, useJobsSearchCount } from '@/components/jobs/search'
import { useOrderedJobColumns } from '@/components/jobs/columns'
import { useJobSideSheetNavigation } from '@/components/jobs/navigation'
import { useJobsCountQuery, useJobsQuery } from '@/components/jobs/query'
import { useDailyViewJobQueueFilters } from '@/components/jobs/filters'
import { usePagination } from '@/components/jobs/pagination'
import PriorityFilter from '@/components/common/PriorityFilter.vue'
import ColumnSelector from '@/components/common/ColumnSelector.vue'
import JobColumn from '@/components/jobs/JobColumn'
import TableSearch from '@/components/common/TableSearch.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { until } from '@vueuse/core'
import { useScheduleFilterStore } from '@/components/schedule/ScheduleFilterStore'
import { useFilteredJobStoreLegacyAdapter } from '../jobs/job-search-page/filteredJobStore'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'
import { useEnrichJobsWithProjects } from '@/api/useEnrichJobsWithProjects'

document.title = 'Daily Jobs - IronSight'

const showSnackbar = ref(false)
const filters = useDailyViewJobQueueFilters()
const { getCurrentPage, updatePage } = usePagination()

const { headers } = useOrderedJobColumns()
const { saving: jobIsSaving } = useState('jobs', ['saving'])

// -------
// Queries
// -------
const query = ref(useJobsQuery(filters, getCurrentPage, ref(20)))
const { jobs, error: searchError, isFetching, reFetch } = useJobsSearch(query)

const countQuery = ref(useJobsCountQuery(filters))
const { count, countFetching, countRefetch } = useJobsSearchCount(countQuery)

const { selectedPriorities } = useFilteredJobStoreLegacyAdapter()
const { selectedJobNumber, handleRowClicked } = useJobSideSheetNavigation(
  'day-view',
  'sv-ticket-info-panel',
)

// -------
// store
// -------
const filterStore = useFilteredJobStoreLegacyAdapter()
const {
  updateSearch,
  updateFilterReset,
  updateShowStandbyUnits,
  updateShowOfflineUnits,
  search: scheduleFilterSearch,
} = useScheduleFilterStore()

const { isFetched: isFetchedFilterOptions } = useGetJobFilterOptions()

// -------
// Functions
// -------
const resetAllDayViewFilters = async () => {
  await until(isFetchedFilterOptions).toBe(true)

  updateSearch('')

  filterStore.$patch({
    selectedPriorities: [],
  })

  updateFilterReset(true)
  updateShowStandbyUnits(true)
  updateShowOfflineUnits(true)
}

function refreshJobs() {
  reFetch()
  countRefetch()
}

const handleUpdateSearch = (value: string) => {
  updateSearch(value)
  updatePage(1)
}

const handleUpdatePriorities = (value: []) => {
  filterStore.$patch({
    selectedPriorities: value,
  })
  updatePage(1)
}

const isProjectColumnVisible = computed(() =>
  headers.value.some((column) => !column.isCustom && column.value === 'project'),
)
const { enrichedJobs } = useEnrichJobsWithProjects(jobs, { enabled: isProjectColumnVisible })

// -------
// Watchers
// -------
watchEffect(() => {
  showSnackbar.value = !!searchError.value
})
watch(selectedPriorities, () => updatePage(1))
watch(
  Object.values(filters).filter((x) => x === 'priorities'),
  () => {
    updatePage(1)
  },
)
watch(jobIsSaving, (isSaving, wasSaving) => {
  if (wasSaving && !isSaving) {
    refreshJobs()
  }
})
</script>

<style scoped>
.job-queue {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-bottom: 1px solid var(--color-grey--lighter);
}

.job-queue-header {
  padding-top: 5px;
}

.job-queue-footer {
  padding: var(--space-smaller) var(--space-large) !important;
  border-top: 1px solid var(--color-grey--lighter);
}

.jobs-table {
  flex: auto;
  overflow: hidden;
}

.jobs-table >>> .v-data-table__wrapper {
  height: 100%;
}

.jobs-table >>> th[role='columnheader'] {
  white-space: nowrap;
  height: auto !important;
  z-index: var(--elevation-base);
  padding: var(--space-small) var(--space-large) !important;
}

.jobs-table >>> table tbody tr td {
  padding: var(--space-base) var(--space-large) !important;
  white-space: nowrap;
  height: auto;
}

.jobs-table-row-selected {
  background-color: var(--color-grey--lightest);
}
</style>
